import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Button, Empty, Spin, Alert, Row, Col,
  Popconfirm, Input, message, Modal, theme as antdTheme,
} from 'antd';
import {
  MailOutlined, DeleteOutlined, SearchOutlined,
  PlusOutlined, FileTextOutlined, EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const Templates = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [templates,    setTemplates]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');
  const [previewTpl,   setPreviewTpl]   = useState(null); // template being previewed

  const navigate = useNavigate();

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/templates');
      setTemplates(res.data);
    } catch {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      message.success('Template deleted');
    } catch {
      message.error('Failed to delete template');
    }
  };

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Email Templates</Title>
          <Text style={{ color: themeToken.colorTextSecondary }}>Saved designs you can reuse across campaigns</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/campaigns/create')}
          style={{ borderRadius: 10, fontWeight: 600 }}
        >
          New Campaign
        </Button>
      </div>

      {error && (
        <Alert message={error} type="error" showIcon closable
          onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 10 }} />
      )}

      {/* Search bar */}
      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Input
            prefix={<SearchOutlined style={{ color: themeToken.colorTextTertiary }} />}
            placeholder="Search templates…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ borderRadius: 10, maxWidth: 360 }}
          />
          <Text style={{ color: themeToken.colorTextTertiary, fontSize: 12 }}>
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
          </Text>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>
      ) : filtered.length === 0 ? (
        <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, textAlign: 'center', padding: '60px 0' }}>
          <Empty
            image={<FileTextOutlined style={{ fontSize: 56, color: '#c4b5fd' }} />}
            imageStyle={{ height: 60 }}
            description={
              <div>
                <Text style={{ fontSize: 15, color: themeToken.colorText, display: 'block', marginBottom: 4 }}>
                  {search ? 'No templates match your search' : 'No templates saved yet'}
                </Text>
                <Text style={{ color: themeToken.colorTextTertiary, fontSize: 13 }}>
                  {search
                    ? 'Try a different search term'
                    : 'Open the email builder and click "Save as Template" to save a design for reuse'}
                </Text>
              </div>
            }
          >
            {!search && (
              <Button
                type="primary" icon={<PlusOutlined />}
                onClick={() => navigate('/campaigns/create')}
                style={{ borderRadius: 10 }}
              >
                Create Campaign
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map(tpl => (
            <Col xs={24} sm={12} lg={8} xl={6} key={tpl.id}>
              <Card
                style={{
                  borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  height: '100%',
                }}
                styles={{ body: { padding: 0 } }}
              >
                {/* Thumbnail preview area — click to open full preview */}
                <div
                  onClick={() => setPreviewTpl(tpl)}
                  style={{
                    height: 180, borderRadius: '14px 14px 0 0',
                    overflow: 'hidden', cursor: 'pointer',
                    background: themeToken.colorBgLayout, position: 'relative',
                    border: '0',
                  }}
                >
                  {tpl.html ? (
                    <iframe
                      srcDoc={tpl.html}
                      title={tpl.name}
                      sandbox="allow-same-origin"
                      scrolling="no"
                      style={{
                        width: '600px', height: '900px',
                        border: 'none', pointerEvents: 'none',
                        transformOrigin: 'top left',
                        transform: 'scale(0.38)',
                      }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <MailOutlined style={{ fontSize: 40, color: '#c4b5fd' }} />
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(99,102,241,0.0)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.2s',
                    borderRadius: '14px 14px 0 0',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.12)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0)'}
                  >
                    <EyeOutlined style={{ fontSize: 26, color: '#6366f1', opacity: 0, transition: 'opacity 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    />
                  </div>
                </div>

                {/* Card body */}
                <div style={{ padding: '14px 16px' }}>
                  <Text strong style={{
                    fontSize: 13, display: 'block',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    marginBottom: 2,
                  }}>
                    {tpl.name}
                  </Text>
                  <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary, display: 'block', marginBottom: 14 }}>
                    {new Date(tpl.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </Text>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewTpl(tpl)}
                      style={{ borderRadius: 8, fontSize: 12, flex: 1 }}
                    >
                      Preview
                    </Button>
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => navigate('/campaigns/create', { state: { templateDesign: tpl.design_json } })}
                      style={{ borderRadius: 8, fontSize: 12, flex: 1, borderColor: themeToken.colorPrimary, color: themeToken.colorPrimary }}
                    >
                      Use
                    </Button>
                    <Popconfirm
                      title="Delete this template?"
                      onConfirm={() => handleDelete(tpl.id)}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                    >
                      <Button
                        size="small" danger icon={<DeleteOutlined />}
                        style={{ borderRadius: 8, flexShrink: 0 }}
                      />
                    </Popconfirm>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ── Full preview modal ── */}
      <Modal
        open={!!previewTpl}
        onCancel={() => setPreviewTpl(null)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
            <span>{previewTpl?.name}</span>
            <Button
              type="primary" size="small" icon={<PlusOutlined />}
              onClick={() => {
                navigate('/campaigns/create', { state: { templateDesign: previewTpl.design_json } });
                setPreviewTpl(null);
              }}
              style={{ borderRadius: 8, fontSize: 12 }}
            >
              Use Template
            </Button>
          </div>
        }
        footer={null}
        width="80vw"
        style={{ top: 24 }}
        styles={{ body: { padding: 0, height: '78vh', overflow: 'hidden' } }}
        destroyOnHidden
      >
        {previewTpl?.html ? (
          <iframe
            srcDoc={previewTpl.html}
            title={previewTpl.name}
            sandbox="allow-same-origin"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Empty description="No preview available for this template" />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Templates;
